import React, { useState } from 'react';
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronRight,
  Columns3,
  Filter,
  Hash,
  Layers,
  List,
  ListTree,
  Sigma,
  ToggleLeft,
  Type,
} from 'lucide-react';

const TYPE_ICON = {
  string: Type,
  enum: List,
  integer: Hash,
  number: Hash,
  money: Hash,
  date: Calendar,
  datetime: Calendar,
  boolean: ToggleLeft,
};

const ACTION_BTN = 'p-1 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400';

function FieldRow({ field, selected, onAddColumn, onAddGroup, onAddFilter, groupingActive, depth }) {
  const Icon = TYPE_ICON[field.type] || Type;
  const inColumns = selected.columns.has(field.id);
  const inGroup = selected.groupBy.has(field.id);
  const filterOnly = !!field.filter_only;
  const canGroup = field.dimension !== false && !filterOnly;
  const canColumn = field.dimension !== false && !filterOnly && !groupingActive;
  return (
    <div className="group flex items-center gap-2 py-1 pr-1 rounded hover:bg-gray-50" style={{ paddingLeft: 8 + depth * 14 }}>
      <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      <span className={`flex-1 text-sm truncate ${filterOnly ? 'text-gray-500 italic' : 'text-gray-800'}`} title={field.description || field.label}>
        {field.label}
        {filterOnly && <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-400">filter only</span>}
      </span>
      <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
        {canColumn && (
          <button type="button" className={ACTION_BTN} title="Add as column" disabled={inColumns} onClick={() => onAddColumn(field.id)}>
            <Columns3 className="w-3.5 h-3.5" />
          </button>
        )}
        {canGroup && (
          <button type="button" className={ACTION_BTN} title="Group by this field" disabled={inGroup} onClick={() => onAddGroup(field.id)}>
            <Layers className="w-3.5 h-3.5" />
          </button>
        )}
        {field.filterable !== false && (
          <button type="button" className={ACTION_BTN} title="Add a filter on this field" onClick={() => onAddFilter(field.id, field.type)}>
            <Filter className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}

function RollupRow({ rollup, selected, onAddColumn, onAddMeasure, onAddFilter, groupingActive, depth }) {
  const inColumns = selected.columns.has(rollup.id);
  return (
    <div className="group flex items-center gap-2 py-1 pr-1 rounded hover:bg-gray-50" style={{ paddingLeft: 8 + depth * 14 }}>
      <Sigma className="w-3.5 h-3.5 text-purple-400 shrink-0" />
      <span className="flex-1 text-sm text-gray-800 truncate" title={rollup.description || `${rollup.agg} · ${rollup.type}`}>
        {rollup.label}
      </span>
      <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
        {!groupingActive && (
          <button type="button" className={ACTION_BTN} title="Add as column (per row)" disabled={inColumns} onClick={() => onAddColumn(rollup.id)}>
            <Columns3 className="w-3.5 h-3.5" />
          </button>
        )}
        <button type="button" className={ACTION_BTN} title="Add as a summed measure" onClick={() => onAddMeasure(rollup.id)}>
          <Sigma className="w-3.5 h-3.5" />
        </button>
        <button type="button" className={ACTION_BTN} title="Filter on this rollup" onClick={() => onAddFilter(rollup.id, rollup.type)}>
          <Filter className="w-3.5 h-3.5" />
        </button>
      </span>
    </div>
  );
}

function RelationNode({ node, depth, defaultOpen, ...rest }) {
  const [open, setOpen] = useState(!!defaultOpen || !!node.forceOpen);
  const isOpen = open || !!node.forceOpen;
  const Card = node.cardinality === 'one_to_many' ? ListTree : ArrowRight;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1.5 py-1.5 pr-1 text-left rounded hover:bg-gray-50"
        style={{ paddingLeft: 4 + depth * 14 }}
        title={node.description || node.label}
      >
        {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
        <Card className={`w-3.5 h-3.5 ${node.cardinality === 'one_to_many' ? 'text-purple-500' : 'text-blue-500'}`} />
        <span className="text-sm font-medium text-gray-700 truncate">{node.label}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-400">{node.cardinality === 'one_to_many' ? 'many' : 'one'}</span>
      </button>
      {isOpen && (
        <div>
          {(node.rollups || []).map((r) => (
            <RollupRow key={r.id} rollup={r} depth={depth + 1} {...rest} />
          ))}
          {(node.fields || []).map((f) => (
            <FieldRow key={f.id} field={f} depth={depth + 1} {...rest} />
          ))}
          {(node.relations || []).map((child) => (
            <RelationNode key={child.path} node={child} depth={depth + 1} {...rest} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The dataset's fields as an expandable tree. Many-to-one relations nest to
 * any depth; one-to-many relations show rollups (Σ) and filter-only fields.
 * Every row is a button so the tree works from the keyboard.
 */
const FieldTree = ({ dataset, selected, onAddColumn, onAddGroup, onAddFilter, onAddMeasure, groupingActive }) => {
  if (!dataset) return null;
  const rowProps = { selected, onAddColumn, onAddGroup, onAddFilter, onAddMeasure, groupingActive };
  const hasAnything = (dataset.fields || []).length || (dataset.relations || []).length;
  if (!hasAnything) {
    return <p className="text-sm text-gray-500 px-2 py-4">No fields match your search.</p>;
  }
  return (
    <div className="text-sm">
      {(dataset.fields || []).map((f) => (
        <FieldRow key={f.id} field={f} depth={0} {...rowProps} />
      ))}
      {(dataset.relations || []).map((rel) => (
        <RelationNode key={rel.path} node={rel} depth={0} defaultOpen={false} {...rowProps} />
      ))}
    </div>
  );
};

export default FieldTree;
